import type { LoomAirfoil } from "./loomAuthoringModel";

export type LoomWingSectionSide = "root" | "tip";
export type LoomHighLiftDeviceKind = "plain-flap" | "slat" | "krueger";
export interface LoomNumericLimit { min: number; max: number }
export interface LoomIntegerLimit { min: number; max: number }

export interface LoomSectionControlLimits {
  schema: "loom-section-control-limits/v1";
  allowedFamilies: readonly LoomAirfoil["family"][];
  naca4: {
    camberRatio: LoomNumericLimit;
    camberLocationRatio: LoomNumericLimit;
    thicknessRatio: LoomNumericLimit;
    trailingEdgeThicknessRatio: LoomNumericLimit;
  };
  cst: { coefficient: LoomNumericLimit; order: LoomIntegerLimit };
  polygon: { xRatio: LoomNumericLimit; zRatio: LoomNumericLimit; pointCount: LoomIntegerLimit };
  highLift: {
    allowedDeviceKinds: readonly LoomHighLiftDeviceKind[];
    allowDroopNose: boolean;
    maxDevices: number;
    hingeXRatio: LoomNumericLimit;
    deflectionDeg: LoomNumericLimit;
    gapRatio: LoomNumericLimit;
    overlapRatio: LoomNumericLimit;
  };
}

export interface LoomSectionSourceIdentity {
  documentId: string;
  stateId: string;
  revision: number;
  nodeId: string;
  panelId: string;
  side: LoomWingSectionSide;
  sectionId: string;
}

export interface LoomHighLiftDeviceDto {
  schema: "loom-high-lift-device/v1";
  elementId: string;
  kind: LoomHighLiftDeviceKind;
  hingeXRatio: number;
  deflectionDeg: number;
  gapRatio: number;
  overlapRatio: number;
}

/** Continuous main-element morph; gap and overlap are intentionally inapplicable. */
export interface LoomDroopNoseMorphDto {
  schema: "loom-high-lift-leading-edge-morph/v1";
  elementId: string;
  kind: "droop-nose";
  hingeXRatio: number;
  deflectionDeg: number;
}

export interface LoomSectionArtifactReference {
  schema: "loom-section-artifact-ref/v1";
  artifactId: `sha256:${string}`;
  payloadDigest: `fnv1a32:${string}`;
  producer: "thread-high-lift";
  producerVersion: string;
}

export interface LoomHighLiftTreatment {
  schema: "loom-high-lift-treatment/v1";
  source: LoomSectionSourceIdentity;
  artifact: LoomSectionArtifactReference;
  devices: readonly LoomHighLiftDeviceDto[];
  leadingEdgeMorph?: LoomDroopNoseMorphDto;
}

export interface LoomSectionRecipe {
  schema: "loom-section-recipe/v1";
  limits: LoomSectionControlLimits;
  treatment?: LoomHighLiftTreatment;
}

export interface LoomWingSectionRecipes {
  root?: LoomSectionRecipe;
  tip?: LoomSectionRecipe;
}

export const DEFAULT_LOOM_SECTION_CONTROL_LIMITS: LoomSectionControlLimits = immutableLoomSectionValue({
  schema: "loom-section-control-limits/v1",
  allowedFamilies: ["naca4", "cst", "polygon"],
  naca4: {
    camberRatio: { min: 0, max: 0.12 },
    camberLocationRatio: { min: 0, max: 0.9 },
    thicknessRatio: { min: 0.03, max: 0.3 },
    trailingEdgeThicknessRatio: { min: 0, max: 0.05 },
  },
  cst: { coefficient: { min: -2, max: 2 }, order: { min: 2, max: 12 } },
  polygon: { xRatio: { min: 0, max: 1 }, zRatio: { min: -0.5, max: 0.5 }, pointCount: { min: 8, max: 512 } },
  highLift: {
    allowedDeviceKinds: ["plain-flap", "slat", "krueger"],
    allowDroopNose: true,
    maxDevices: 8,
    hingeXRatio: { min: 0.05, max: 0.95 },
    deflectionDeg: { min: -90, max: 90 },
    gapRatio: { min: 0, max: 0.2 },
    overlapRatio: { min: -0.25, max: 0.25 },
  },
});

type RecordValue = Record<string, unknown>;
function object(value: unknown, path: string): RecordValue {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as RecordValue;
}
function exact(value: RecordValue, keys: readonly string[], path: string): void {
  const unknown = Object.keys(value).filter((key) => !keys.includes(key));
  if (unknown.length) throw new Error(`${path} contains unknown fields: ${unknown.join(", ")}`);
}
function text(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${path} must be a non-empty string`);
  return value;
}
function stableId(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,511}$/.test(result)) throw new Error(`${path} is not a stable id`);
  return result;
}
function finite(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path} must be finite`);
  return value;
}
function integer(value: unknown, path: string, min = 0): number {
  const result = finite(value, path);
  if (!Number.isInteger(result) || result < min) throw new Error(`${path} must be an integer >= ${min}`);
  return result;
}
function choice<T extends string>(value: unknown, choices: readonly T[], path: string): T {
  if (typeof value !== "string" || !choices.includes(value as T)) throw new Error(`${path} is unsupported`);
  return value as T;
}
function bool(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${path} must be boolean`);
  return value;
}
function range(value: unknown, path: string, integral = false): LoomNumericLimit | LoomIntegerLimit {
  const item = object(value, path); exact(item, ["min", "max"], path);
  const min = integral ? integer(item.min, `${path}.min`) : finite(item.min, `${path}.min`);
  const max = integral ? integer(item.max, `${path}.max`) : finite(item.max, `${path}.max`);
  if (max < min) throw new Error(`${path}.max must be >= min`);
  return { min, max };
}
function inRange(value: number, limit: LoomNumericLimit, path: string): void {
  if (value < limit.min || value > limit.max) throw new Error(`${path} is outside its Loom control limit`);
}
function domain(value: unknown, min: number, max: number, path: string): number {
  const result = finite(value, path);
  if (result < min || result > max) throw new Error(`${path} is outside its interop domain`);
  return result;
}

export function immutableLoomSectionValue<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  Object.values(value as RecordValue).forEach(immutableLoomSectionValue);
  return Object.freeze(value);
}

export function canonicalLoomSectionJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalLoomSectionJson).join(",")}]`;
  if (typeof value === "object" && value !== null) {
    const record = value as RecordValue;
    return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${canonicalLoomSectionJson(record[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

export function loomHighLiftPayloadDigest(
  source: LoomSectionSourceIdentity,
  baseSection: LoomAirfoil,
  devices: readonly LoomHighLiftDeviceDto[],
  leadingEdgeMorph?: LoomDroopNoseMorphDto,
): `fnv1a32:${string}` {
  let hash = 0x811c9dc5;
  const payload = canonicalLoomSectionJson({ source, baseSection, devices, ...(leadingEdgeMorph ? { leadingEdgeMorph } : {}) });
  for (const character of payload) { hash ^= character.charCodeAt(0); hash = Math.imul(hash, 0x01000193); }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export function loomPatternSectionId(source: Omit<LoomSectionSourceIdentity, "sectionId">): string {
  return `loom-section:${source.documentId}:${source.stateId}:${source.nodeId}:${source.panelId}:${source.side}`;
}

export function parseLoomSectionSourceIdentity(value: unknown, path: string): LoomSectionSourceIdentity {
  const item = object(value, path); exact(item, ["documentId", "stateId", "revision", "nodeId", "panelId", "side", "sectionId"], path);
  const source = { documentId: stableId(item.documentId, `${path}.documentId`), stateId: stableId(item.stateId, `${path}.stateId`), revision: integer(item.revision, `${path}.revision`), nodeId: stableId(item.nodeId, `${path}.nodeId`), panelId: stableId(item.panelId, `${path}.panelId`), side: choice(item.side, ["root", "tip"] as const, `${path}.side`) };
  const sectionId = stableId(item.sectionId, `${path}.sectionId`);
  if (sectionId !== loomPatternSectionId(source)) throw new Error(`${path}.sectionId does not match its source`);
  return { ...source, sectionId };
}

export function parseLoomSectionControlLimits(value: unknown, path = "limits"): LoomSectionControlLimits {
  const item = object(value, path); exact(item, ["schema", "allowedFamilies", "naca4", "cst", "polygon", "highLift"], path);
  if (item.schema !== "loom-section-control-limits/v1" || !Array.isArray(item.allowedFamilies)) throw new Error(`${path} schema or families are invalid`);
  const allowedFamilies = item.allowedFamilies.map((entry, index) => choice(entry, ["naca4", "cst", "polygon"] as const, `${path}.allowedFamilies[${index}]`));
  if (!allowedFamilies.length || new Set(allowedFamilies).size !== allowedFamilies.length) throw new Error(`${path}.allowedFamilies must be non-empty and unique`);
  const naca = object(item.naca4, `${path}.naca4`); exact(naca, ["camberRatio", "camberLocationRatio", "thicknessRatio", "trailingEdgeThicknessRatio"], `${path}.naca4`);
  const cst = object(item.cst, `${path}.cst`); exact(cst, ["coefficient", "order"], `${path}.cst`);
  const polygon = object(item.polygon, `${path}.polygon`); exact(polygon, ["xRatio", "zRatio", "pointCount"], `${path}.polygon`);
  const highLift = object(item.highLift, `${path}.highLift`); exact(highLift, ["allowedDeviceKinds", "allowDroopNose", "maxDevices", "hingeXRatio", "deflectionDeg", "gapRatio", "overlapRatio"], `${path}.highLift`);
  if (!Array.isArray(highLift.allowedDeviceKinds)) throw new Error(`${path}.highLift.allowedDeviceKinds must be an array`);
  const allowedDeviceKinds = highLift.allowedDeviceKinds.map((entry, index) => choice(entry, ["plain-flap", "slat", "krueger"] as const, `${path}.highLift.allowedDeviceKinds[${index}]`));
  if (new Set(allowedDeviceKinds).size !== allowedDeviceKinds.length) throw new Error(`${path}.highLift.allowedDeviceKinds must be unique`);
  return immutableLoomSectionValue({ schema: "loom-section-control-limits/v1", allowedFamilies, naca4: { camberRatio: range(naca.camberRatio, `${path}.naca4.camberRatio`), camberLocationRatio: range(naca.camberLocationRatio, `${path}.naca4.camberLocationRatio`), thicknessRatio: range(naca.thicknessRatio, `${path}.naca4.thicknessRatio`), trailingEdgeThicknessRatio: range(naca.trailingEdgeThicknessRatio, `${path}.naca4.trailingEdgeThicknessRatio`) }, cst: { coefficient: range(cst.coefficient, `${path}.cst.coefficient`), order: range(cst.order, `${path}.cst.order`, true) }, polygon: { xRatio: range(polygon.xRatio, `${path}.polygon.xRatio`), zRatio: range(polygon.zRatio, `${path}.polygon.zRatio`), pointCount: range(polygon.pointCount, `${path}.polygon.pointCount`, true) }, highLift: { allowedDeviceKinds, allowDroopNose: bool(highLift.allowDroopNose, `${path}.highLift.allowDroopNose`), maxDevices: integer(highLift.maxDevices, `${path}.highLift.maxDevices`, 1), hingeXRatio: range(highLift.hingeXRatio, `${path}.highLift.hingeXRatio`), deflectionDeg: range(highLift.deflectionDeg, `${path}.highLift.deflectionDeg`), gapRatio: range(highLift.gapRatio, `${path}.highLift.gapRatio`), overlapRatio: range(highLift.overlapRatio, `${path}.highLift.overlapRatio`) } });
}

export function validateLoomAirfoilAgainstLimits(section: LoomAirfoil, limits: LoomSectionControlLimits): void {
  if (!limits.allowedFamilies.includes(section.family)) throw new Error(`section family ${section.family} is not allowed`);
  if (section.family === "naca4") { inRange(section.camberRatio, limits.naca4.camberRatio, "section.camberRatio"); inRange(section.camberLocationRatio, limits.naca4.camberLocationRatio, "section.camberLocationRatio"); inRange(section.thicknessRatio, limits.naca4.thicknessRatio, "section.thicknessRatio"); inRange(section.trailingEdgeThicknessRatio, limits.naca4.trailingEdgeThicknessRatio, "section.trailingEdgeThicknessRatio"); }
  else if (section.family === "cst") { inRange(section.upperCoefficients.length, limits.cst.order, "section.cst.order"); [...section.upperCoefficients, ...section.lowerCoefficients].forEach((coefficient) => inRange(coefficient, limits.cst.coefficient, "section.cst.coefficient")); }
  else { inRange(section.vertices.length, limits.polygon.pointCount, "section.polygon.pointCount"); section.vertices.forEach(([x, z]) => { inRange(x, limits.polygon.xRatio, "section.polygon.xRatio"); inRange(z, limits.polygon.zRatio, "section.polygon.zRatio"); }); inRange(section.trailingEdgeThicknessRatio, limits.naca4.trailingEdgeThicknessRatio, "section.trailingEdgeThicknessRatio"); }
}

export function parseLoomHighLiftDevices(value: unknown, path: string): readonly LoomHighLiftDeviceDto[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  const devices = value.map((raw, index): LoomHighLiftDeviceDto => {
    const at = `${path}[${index}]`; const item = object(raw, at); exact(item, ["schema", "elementId", "kind", "hingeXRatio", "deflectionDeg", "gapRatio", "overlapRatio"], at);
    if (item.schema !== "loom-high-lift-device/v1") throw new Error(`${at}.schema must be loom-high-lift-device/v1`);
    return { schema: "loom-high-lift-device/v1", elementId: stableId(item.elementId, `${at}.elementId`), kind: choice(item.kind, ["plain-flap", "slat", "krueger"] as const, `${at}.kind`), hingeXRatio: domain(item.hingeXRatio, 0, 1, `${at}.hingeXRatio`), deflectionDeg: domain(item.deflectionDeg, -180, 180, `${at}.deflectionDeg`), gapRatio: domain(item.gapRatio, 0, 1, `${at}.gapRatio`), overlapRatio: domain(item.overlapRatio, -1, 1, `${at}.overlapRatio`) };
  });
  if (new Set(devices.map(({ elementId }) => elementId)).size !== devices.length) throw new Error(`${path} contains duplicate elementId values`);
  return immutableLoomSectionValue(devices);
}

export function parseLoomDroopNoseMorph(value: unknown, path: string): LoomDroopNoseMorphDto {
  const item = object(value, path); exact(item, ["schema", "elementId", "kind", "hingeXRatio", "deflectionDeg"], path);
  if (item.schema !== "loom-high-lift-leading-edge-morph/v1" || item.kind !== "droop-nose") throw new Error(`${path} schema or kind is invalid`);
  return immutableLoomSectionValue({ schema: "loom-high-lift-leading-edge-morph/v1", elementId: stableId(item.elementId, `${path}.elementId`), kind: "droop-nose", hingeXRatio: domain(item.hingeXRatio, 0, 1, `${path}.hingeXRatio`), deflectionDeg: domain(item.deflectionDeg, -180, 180, `${path}.deflectionDeg`) });
}

export function validateLoomTreatmentAgainstLimits(devices: readonly LoomHighLiftDeviceDto[], morph: LoomDroopNoseMorphDto | undefined, limits: LoomSectionControlLimits): void {
  if (devices.length > limits.highLift.maxDevices) throw new Error("treatment.devices exceeds its Loom control limit");
  devices.forEach((device) => { if (!limits.highLift.allowedDeviceKinds.includes(device.kind)) throw new Error(`device kind ${device.kind} is not allowed`); inRange(device.hingeXRatio, limits.highLift.hingeXRatio, `device ${device.elementId}.hingeXRatio`); inRange(device.deflectionDeg, limits.highLift.deflectionDeg, `device ${device.elementId}.deflectionDeg`); inRange(device.gapRatio, limits.highLift.gapRatio, `device ${device.elementId}.gapRatio`); inRange(device.overlapRatio, limits.highLift.overlapRatio, `device ${device.elementId}.overlapRatio`); });
  if (morph) { if (!limits.highLift.allowDroopNose) throw new Error("droop-nose morph is not allowed"); inRange(morph.hingeXRatio, limits.highLift.hingeXRatio, `morph ${morph.elementId}.hingeXRatio`); inRange(morph.deflectionDeg, limits.highLift.deflectionDeg, `morph ${morph.elementId}.deflectionDeg`); if (devices.some(({ elementId }) => elementId === morph.elementId)) throw new Error("treatment element IDs must be unique across devices and morph"); }
}

export function parseLoomSectionArtifactReference(value: unknown, path: string): LoomSectionArtifactReference {
  const item = object(value, path); exact(item, ["schema", "artifactId", "payloadDigest", "producer", "producerVersion"], path);
  if (item.schema !== "loom-section-artifact-ref/v1" || item.producer !== "thread-high-lift") throw new Error(`${path} schema or producer is invalid`);
  const artifactId = text(item.artifactId, `${path}.artifactId`); if (!/^sha256:[a-f0-9]{64}$/.test(artifactId)) throw new Error(`${path}.artifactId must be a sha256 content id`);
  const payloadDigest = text(item.payloadDigest, `${path}.payloadDigest`); if (!/^fnv1a32:[a-f0-9]{8}$/.test(payloadDigest)) throw new Error(`${path}.payloadDigest is invalid`);
  return immutableLoomSectionValue({ schema: "loom-section-artifact-ref/v1", artifactId: artifactId as `sha256:${string}`, payloadDigest: payloadDigest as `fnv1a32:${string}`, producer: "thread-high-lift", producerVersion: text(item.producerVersion, `${path}.producerVersion`) });
}

export function parseLoomSectionRecipe(value: unknown, baseSection: LoomAirfoil, path: string): LoomSectionRecipe {
  const item = object(value, path); exact(item, ["schema", "limits", "treatment"], path); if (item.schema !== "loom-section-recipe/v1") throw new Error(`${path}.schema must be loom-section-recipe/v1`);
  const limits = parseLoomSectionControlLimits(item.limits, `${path}.limits`); validateLoomAirfoilAgainstLimits(baseSection, limits);
  if (item.treatment === undefined) return immutableLoomSectionValue({ schema: "loom-section-recipe/v1", limits });
  const treatment = object(item.treatment, `${path}.treatment`); exact(treatment, ["schema", "source", "artifact", "devices", "leadingEdgeMorph"], `${path}.treatment`); if (treatment.schema !== "loom-high-lift-treatment/v1") throw new Error(`${path}.treatment.schema must be loom-high-lift-treatment/v1`);
  const source = parseLoomSectionSourceIdentity(treatment.source, `${path}.treatment.source`); const devices = parseLoomHighLiftDevices(treatment.devices, `${path}.treatment.devices`); const leadingEdgeMorph = treatment.leadingEdgeMorph === undefined ? undefined : parseLoomDroopNoseMorph(treatment.leadingEdgeMorph, `${path}.treatment.leadingEdgeMorph`); validateLoomTreatmentAgainstLimits(devices, leadingEdgeMorph, limits); const artifact = parseLoomSectionArtifactReference(treatment.artifact, `${path}.treatment.artifact`);
  if (artifact.payloadDigest !== loomHighLiftPayloadDigest(source, baseSection, devices, leadingEdgeMorph)) throw new Error(`${path}.treatment.artifact.payloadDigest does not match treatment content`);
  return immutableLoomSectionValue({ schema: "loom-section-recipe/v1", limits, treatment: { schema: "loom-high-lift-treatment/v1", source, artifact, devices, ...(leadingEdgeMorph ? { leadingEdgeMorph } : {}) } });
}

export function parseLoomWingSectionRecipes(value: unknown, baseSections: { root: LoomAirfoil; tip: LoomAirfoil }, path: string): LoomWingSectionRecipes {
  const item = object(value, path); exact(item, ["root", "tip"], path); if (item.root === undefined && item.tip === undefined) throw new Error(`${path} must define root or tip`);
  return immutableLoomSectionValue({ ...(item.root === undefined ? {} : { root: parseLoomSectionRecipe(item.root, baseSections.root, `${path}.root`) }), ...(item.tip === undefined ? {} : { tip: parseLoomSectionRecipe(item.tip, baseSections.tip, `${path}.tip`) }) });
}
